import { useEffect, useState } from 'react';
import {
  OTPInput,
  OTPInputStatuses,
  Button,
  ButtonVariants,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { isOtpComplete } from '../authValidation';
import styles from './OtpForm.module.scss';
import { useTranslations } from '@/hooks';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Props:
 *   email             the address the code was sent to (display only)
 *   onSubmit          (code: string) => void
 *   onResend          () => void
 *   isLoading         true while the code-submit call is in flight
 *   isResending       true while the resend call is in flight
 *   error             RTK Query error from either call, or undefined
 *   resendNotice      show the "code re-sent" success banner
 *   onDismissResendNotice  () => void
 */
export const OtpForm = ({
  email,
  onSubmit,
  onResend,
  isLoading,
  isResending,
  error,
  resendNotice,
  onDismissResendNotice,
}) => {
  const t = useTranslations();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isOtpComplete(code, OTP_LENGTH)) return;
    onSubmit(code);
  };

  const handleResend = () => {
    setCode('');
    setCooldown(RESEND_COOLDOWN_SECONDS);
    onResend();
  };

  return (
    <form className={styles.root} onSubmit={handleSubmit} noValidate>
      <div className={styles.intro}>
        <Typography variant={TypographyVariants.h6} as={TypographyElements.h1}>
          {t('AUTH_OTP_TITLE')}
        </Typography>
        <Typography variant={TypographyVariants.body} color={TypographyColors.primary}>
          {t('AUTH_OTP_SENT_TO', { email })}
        </Typography>
      </div>

      {resendNotice && (
        <Alert
          variant={AlertVariants.success}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
          onDismiss={onDismissResendNotice}
          className={styles.alert}
        >
          {t('AUTH_OTP_RESENT')}
        </Alert>
      )}

      {error && (
        <Alert
          variant={AlertVariants.danger}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
          className={styles.alert}
        >
          {error.data?.message ?? t('ERROR_GENERIC')}
        </Alert>
      )}

      <OTPInput
        label={t('AUTH_OTP_LABEL')}
        length={OTP_LENGTH}
        value={code}
        onChange={setCode}
        status={error ? OTPInputStatuses.error : OTPInputStatuses.default}
        disabled={isLoading}
        // OTPInput applies this in an effect, not as the native attribute.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />

      <div className={styles.actions}>
        <Button
          type="submit"
          fullWidth
          size={ButtonSizes.sm}
          loading={isLoading}
          disabled={!isOtpComplete(code, OTP_LENGTH)}
        >
          {t('ACTION_CONTINUE')}
        </Button>

        <div className={styles.resendRow}>
          <Typography
            variant={TypographyVariants.body}
            color={TypographyColors.primary}
            as={TypographyElements.span}
          >
            {t('AUTH_OTP_NO_CODE')}
          </Typography>
          <Button
            type="button"
            variant={ButtonVariants.tertiary}
            size={ButtonSizes.sm}
            className={styles.resendButton}
            disabled={cooldown > 0 || isResending}
            loading={isResending}
            onClick={handleResend}
          >
            {cooldown > 0 ? t('AUTH_RESEND_IN', { seconds: cooldown }) : t('AUTH_RESEND_PROMPT')}
          </Button>
        </div>
      </div>
    </form>
  );
};
